/**
 * Upload Utilities
 *
 * Helper functions for video upload functionality including:
 * - Wake lock management
 * - Timeout calculation
 * - Error message formatting
 * - Error type detection
 *
 * @module upload-utils
 */

import uploadState from './upload-state'
import {
  UPLOAD_CONFIG,
  NETWORK_ERROR_PATTERNS,
  LOG_EMOJI,
} from './upload-config'

// ========== Wake Lock Management ==========

/**
 * Request wake lock to prevent device from sleeping during upload
 * @param {string} uploadId - Upload identifier
 * @returns {Promise<WakeLockSentinel|null>}
 */
export async function requestWakeLock(uploadId) {
  // Check if Wake Lock API is supported
  if ('wakeLock' in navigator) {
    try {
      const wakeLock = await navigator.wakeLock.request('screen')
      uploadState.setWakeLock(uploadId, wakeLock)

      // Listen for wake lock release
      wakeLock.addEventListener('release', () => {
        uploadState.deleteWakeLock(uploadId)
      })

      return wakeLock
    } catch (err) {
      console.warn(
        `${LOG_EMOJI.WARNING} Wake lock request failed: ${err.message}`,
      )
      return null
    }
  } else {
    return null
  }
}

/**
 * Release wake lock after upload completes/fails
 * @param {string} uploadId - Upload identifier
 * @returns {Promise<void>}
 */
export async function releaseWakeLock(uploadId) {
  const wakeLock = uploadState.getWakeLock(uploadId)
  if (wakeLock) {
    try {
      await wakeLock.release()
      uploadState.deleteWakeLock(uploadId)
    } catch (err) {
      console.warn(
        `${LOG_EMOJI.WARNING} Wake lock release failed: ${err.message}`,
      )
    }
  }
}

// ========== Timeout Calculation ==========

/**
 * Calculate dynamic timeout based on file size
 * @param {number} fileSize - File size in bytes
 * @returns {number} Timeout in milliseconds
 */
export function getUploadTimeout(fileSize) {
  const fileSizeMB = fileSize / (1024 * 1024)
  const calculatedTimeout =
    UPLOAD_CONFIG.BASE_TIMEOUT + fileSizeMB * UPLOAD_CONFIG.TIMEOUT_PER_MB
  const timeout = Math.min(calculatedTimeout, UPLOAD_CONFIG.MAX_TIMEOUT)

  return timeout
}

/**
 * Get retry delay for attempt number
 * @param {number} attempt - Current attempt number (0-indexed)
 * @returns {number} Delay in milliseconds
 */
export function getRetryDelay(attempt) {
  if (attempt >= UPLOAD_CONFIG.RETRY_DELAYS.length) {
    return UPLOAD_CONFIG.RETRY_DELAYS[UPLOAD_CONFIG.RETRY_DELAYS.length - 1]
  }
  return UPLOAD_CONFIG.RETRY_DELAYS[attempt]
}

// ========== Error Detection ==========

/**
 * Check if an error is retryable
 * @param {Error} error - The error object
 * @param {number|null} statusCode - HTTP status code if available
 * @returns {boolean}
 */
export function isRetryableError(error, statusCode) {
  // Network errors (no status code)
  if (!statusCode && error.message.includes('Network error')) {
    return true
  }

  // Timeout errors
  if (error.message.includes('timeout')) {
    return true
  }

  // Retryable HTTP status codes
  if (statusCode && UPLOAD_CONFIG.RETRY_STATUS_CODES.includes(statusCode)) {
    return true
  }

  return false
}

/**
 * Check if error is network-related and should wait for reconnection
 * @param {Error} error - The error object
 * @param {number|null} statusCode - HTTP status code if available
 * @returns {boolean}
 */
export function isNetworkIssue(error, statusCode) {
  // Offline
  if (!navigator.onLine) {
    return true
  }

  // Explicit network error flag
  if (error.isNetworkError) {
    return true
  }

  // Status 0 means request was aborted/network issue
  if (statusCode === 0) {
    return true
  }

  // Check for network error patterns in message
  return NETWORK_ERROR_PATTERNS.some(pattern => error.message.includes(pattern))
}

/**
 * Check if error is an abort (user cancellation or system)
 * @param {Error} error - The error object
 * @returns {boolean}
 */
export function isAbortError(error) {
  return error.isAborted || error.message.includes('aborted')
}

// ========== Error Messages ==========

/**
 * Get user-friendly error message
 * @param {Error} error - The error object
 * @param {number|null} statusCode - HTTP status code if available
 * @returns {string}
 */
export function getUserFriendlyErrorMessage(error, statusCode) {
  // Network offline
  if (!navigator.onLine) {
    return 'No internet connection. Upload will resume when connection is restored.'
  }

  // Network errors
  if (
    error.isNetworkError ||
    (!statusCode && error.message.includes('Network error'))
  ) {
    return 'Network connection lost. Retrying...'
  }

  // Timeout errors
  if (error.isTimeout || error.message.includes('timeout')) {
    return 'Upload timed out. Retrying...'
  }

  // Server errors
  if (statusCode && statusCode >= 500) {
    return `Server error (${statusCode}). Retrying...`
  }

  // Too many requests
  if (statusCode === 429) {
    return 'Too many requests. Waiting before retry...'
  }

  // Request timeout
  if (statusCode === 408) {
    return 'Request timed out. Retrying...'
  }

  // Client errors
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return `Upload failed: ${error.message}`
  }

  // Default
  return error.message || 'Upload failed. Please try again.'
}

// ========== File Size Formatting ==========

/**
 * Format bytes to human-readable file size
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string}
 */
export function formatFileSize(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

// ========== Upload Speed Calculation ==========

/**
 * Calculate upload speed in human-readable format
 * @param {number} bytesProcessed - Bytes uploaded
 * @param {number} timeElapsed - Time elapsed in milliseconds
 * @returns {string}
 */
export function calculateUploadSpeed(bytesProcessed, timeElapsed) {
  if (timeElapsed === 0) return '0 KB/s'

  const bytesPerSecond = bytesProcessed / (timeElapsed / 1000)
  return `${formatFileSize(bytesPerSecond)}/s`
}

/**
 * Estimate time remaining for upload
 * @param {number} bytesProcessed - Bytes uploaded
 * @param {number} totalBytes - Total bytes to upload
 * @param {number} timeElapsed - Time elapsed in milliseconds
 * @returns {string}
 */
export function estimateTimeRemaining(bytesProcessed, totalBytes, timeElapsed) {
  if (bytesProcessed === 0 || timeElapsed === 0) {
    return 'Calculating...'
  }

  const bytesPerMs = bytesProcessed / timeElapsed
  const bytesRemaining = totalBytes - bytesProcessed
  const msRemaining = bytesRemaining / bytesPerMs

  const secondsRemaining = Math.round(msRemaining / 1000)

  if (secondsRemaining < 60) {
    return `${secondsRemaining}s remaining`
  } else if (secondsRemaining < 3600) {
    const minutes = Math.round(secondsRemaining / 60)
    return `${minutes}m remaining`
  } else {
    const hours = Math.floor(secondsRemaining / 3600)
    const minutes = Math.round((secondsRemaining % 3600) / 60)
    return `${hours}h ${minutes}m remaining`
  }
}
